/*
 * 4mypet installation logic — product page + cart guard.
 *
 * Works alongside the custom 4mp-roof-options.liquid snippet.
 *
 * Architecture — three-layer approach:
 *
 *  Layer 1 · Button-click capture listener
     *    Intercepts the add-to-cart click whenever:
 *      a) The product has a roof type picker (data-fmp-roof-select), OR
     *      b) The customer has selected installation, OR
     *      c) The product has wall mesh choices or mileage delivery.
 *    When intercepted:
 *      1. Validate roof selection (required when picker exists).
 *      2. If installation selected: validate postcode + async-resolve install variant.
 *      3. Build and send one combined /cart/add.js request containing:
 *           main product (with wall mesh, roof and install properties if applicable)
 *           roof surcharge line (if paid roof selected and variant ID is known)
 *           installation service line (only when installation is selected)
 *      4. Redirect to cart.
 *    With no roof picker, wall mesh, installation or mileage delivery: native form submission runs.
 *
 *  Layer 2 · Fallback form-submit interceptor
 *    Same logic for submit events (accessibility / keyboard triggers).
 *
 *  Layer 3 · Cart guard
 *    Keeps installation service lines in sync when the cart is modified.
 *
 * NOTE: We capture window.fetch BEFORE any other script can shadow it so our
 * own cart/add.js calls always reach the real Shopify API.
 */

(function () {
  "use strict";

  /* Capture the real fetch immediately — before any other script can shadow it */
  var _fetch = window.fetch.bind(window);

  /* ═══════════════════════════════════════════════════════════════
   *  Helpers
   * ═══════════════════════════════════════════════════════════════ */

  function normalisePostcode(v) {
    return (v || "").toUpperCase().trim()
      .replace(/\s+/g, "").replace(/[^A-Z0-9]/g, "");
  }

  function formatPostcodeForStorage(v) {
    var raw = normalisePostcode(v);
    return raw.length <= 3 ? raw : raw.slice(0, -3) + " " + raw.slice(-3);
  }

  function extractPostcodeArea(v) {
    var m = normalisePostcode(v).match(/^([A-Z]{1,2})(?=\d)/);
    return m ? m[1] : null;
  }

  function shopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  /* ═══════════════════════════════════════════════════════════════
   *  Postcode / price validation
   * ═══════════════════════════════════════════════════════════════ */

  function resolvePostcodePrice(opts) {
    var area      = extractPostcodeArea(opts.postcode);
    var formatted = formatPostcodeForStorage(opts.postcode);

    if (!area) return { ok: false, code: "invalid_postcode",
      message: "Please enter a valid UK postcode." };

    var lookup = window.FMP_INSTALL_POSTCODES && window.FMP_INSTALL_POSTCODES[area];
    if (!lookup) return { ok: false, code: "area_not_found",
      message: "Installation is not available online for this postcode area. Please contact us." };

    if (lookup.quoteOnly) return { ok: false, code: "quote_only",
      area: area, postcode: formatted,
      message: "Installation for your area is quoted manually. Please contact us." };

    var price = lookup.oneMan;
    if (!price) return { ok: false, code: "price_missing",
      area: area, postcode: formatted,
      message: "Installation is not priced online for this area. Please contact us." };

    return { ok: true, area: area, postcode: formatted,
      crew: "1man", price: price, region: lookup.region };
  }

  /* ═══════════════════════════════════════════════════════════════
   *  Dynamic install-variant lookup  (/products/<handle>.js)
   * ═══════════════════════════════════════════════════════════════ */

  var _variantCache = {};

  async function fetchInstallVariants(crew) {
    if (_variantCache[crew]) return _variantCache[crew];
    var handle = "installation-1-man";
    var res = await _fetch(shopifyRoot() + "products/" + handle + ".js");
    if (!res.ok) throw new Error("Could not load installation product. Please contact us.");
    var data = await res.json();
    var map = {};
    data.variants.forEach(function (v) {
      if (v.option1) map[v.option1] = v.id;           /* match by Price Band value */
      var p = String(Math.round(v.price / 100));
      if (!map[p]) map[p] = v.id;                     /* fallback: match by price  */
    });
    _variantCache[crew] = map;
    return map;
  }

  async function resolveInstallationForProduct(opts) {
    var pr = resolvePostcodePrice(opts);
    if (!pr.ok) return pr;

    /* Try static map first (manual override) */
    var vid = (
      window.FMP_INSTALL_VARIANTS &&
      window.FMP_INSTALL_VARIANTS[pr.crew] &&
      window.FMP_INSTALL_VARIANTS[pr.crew][String(pr.price)]
    ) || null;

    if (!vid) {
      try {
        var map = await fetchInstallVariants(pr.crew);
        vid = map[String(pr.price)] || null;
      } catch (e) {
        return { ok: false, code: "install_variant_fetch_failed",
          area: pr.area, postcode: pr.postcode, price: pr.price,
          message: e.message || "Could not load installation options. Please try again." };
      }
    }

    if (!vid) return { ok: false, code: "install_variant_missing",
      area: pr.area, postcode: pr.postcode, price: pr.price,
      message: "Could not find an installation variant for \xA3" + pr.price +
        ". Please contact us." };

    return Object.assign({}, pr, { installVariantId: vid });
  }

  /* ═══════════════════════════════════════════════════════════════
   *  Dynamic roof-variant lookup  (/products/<handle>.js)
   *  Used as fallback when a price is absent from the static map.
   * ═══════════════════════════════════════════════════════════════ */

  var _roofVariantCache = null;

  async function fetchRoofVariants() {
    if (_roofVariantCache) return _roofVariantCache;
    var handle = (window.FMP_ROOF_PRODUCT_HANDLE) || "roof-type-upgrade";
    var res = await _fetch(shopifyRoot() + "products/" + handle + ".js");
    if (!res.ok) throw new Error("Could not load roof options. Please contact us.");
    var data = await res.json();
    var map = {};
    data.variants.forEach(function (v) {
      var p = String(Math.round(v.price / 100));
      if (!map[p]) map[p] = v.id;
    });
    _roofVariantCache = map;
    return map;
  }

  /* ═══════════════════════════════════════════════════════════════
   *  Roof option DOM helpers
   *  Read the chosen roof type from our own <select data-fmp-roof-select>.
   * ═══════════════════════════════════════════════════════════════ */

  /**
   * Returns { label, price, variantId } for the currently selected roof option,
   * or null if the roof picker is absent or no value is selected.
   * A £0 option still returns an object (price:0, variantId:null) so the label
   * is recorded as a line-item property.
   *
   * For paid options: tries the static FMP_ROOF_VARIANTS map first; if the price
   * is absent (e.g. a newly-added price band) falls back to a live fetch of the
   * Roof Type Upgrade product so no map regeneration is needed.
   */
  async function readRoofAddon(form) {
    var sel = form.querySelector("[data-fmp-roof-select]");
    if (!sel || sel.disabled || !sel.value) return null;
    var opt   = sel.options[sel.selectedIndex];
    var price = parseFloat(opt.getAttribute("data-fmp-roof-price") || "0") || 0;
    var priceKey = String(Math.round(price));

    var vid = null;

    /* Resolve paid options from the live service product first. This prevents a
       stale theme asset from holding an old variant ID after catalog changes. */
    if (price > 0) {
      try {
        var liveMap = await fetchRoofVariants();
        vid = liveMap[priceKey] || null;
      } catch (e) {
        /* Network fallback below keeps the selector usable during a transient
           product endpoint failure. */
      }
    }

    /* Static map fallback. */
    if (!vid) {
      vid = (
        window.FMP_ROOF_VARIANTS &&
        window.FMP_ROOF_VARIANTS[priceKey]
      ) || null;
    }

    return { label: opt.value, price: price, variantId: vid };
  }

  /**
   * Returns true if the product has no roof picker, OR if the picker has a
   * real (non-placeholder) selection.  False = required but nothing chosen.
   */
  function roofIsValid(form) {
    var sel = form.querySelector("[data-fmp-roof-select]");
    if (!sel || sel.disabled) return true;     /* no picker on this variant */
    return sel.value !== "";   /* must have a real selection */
  }

  /* ═══════════════════════════════════════════════════════════════
   *  Cart API helpers
   * ═══════════════════════════════════════════════════════════════ */

  async function cartAdd(items) {
    var res = await _fetch(shopifyRoot() + "cart/add.js", {
      method:      "POST",
      headers:     { "Content-Type": "application/json" },
      credentials: "same-origin",
      body:        JSON.stringify({ items: items })
    });
    var body = await res.text();
    var data;
    try {
      data = body ? JSON.parse(body) : {};
    } catch (_) {
      data = {};
    }
    if (!res.ok) throw new Error(data.description || data.message || "Could not add to cart");
    return data;
  }

  async function fetchCart() {
    var r = await _fetch(shopifyRoot() + "cart.js", { credentials: "same-origin" });
    if (!r.ok) throw new Error("Could not load cart");
    return r.json();
  }

  async function changeCartLine(line, qty) {
    var r = await _fetch(shopifyRoot() + "cart/change.js", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ line: line, quantity: qty })
    });
    return r.json();
  }

  function isInstallServiceLine(item) {
    return item.properties && item.properties._4mp_install_service === "true";
  }
  function isInstallParentLine(item) {
    return item.properties && item.properties._4mp_install_requested === "yes";
  }
  function getLinkKey(item) {
    return item.properties && item.properties._4mp_install_link;
  }
  function isRoofServiceLine(item) {
    return item.properties && item.properties._4mp_roof_service === "true";
  }
  function isRoofParentLine(item) {
    return item.properties && item.properties._4mp_roof_requested === "yes";
  }
  function getRoofLinkKey(item) {
    return item.properties && item.properties._4mp_roof_link;
  }
  function isDeliveryServiceLine(item) {
    return item.properties && item.properties._4mp_delivery_service === "true";
  }
  function isDeliveryParentLine(item) {
    return item.properties && item.properties._4mp_delivery_requested === "yes";
  }
  function getDeliveryLinkKey(item) {
    return item.properties && item.properties._4mp_delivery_link;
  }

  function buildLinkedCartItems(opts) {
    var items = [
      { id: opts.mainVariantId, quantity: opts.quantity, properties: opts.mainProps }
    ];

    if (opts.hasPaidRoof) {
      items.push({
        id:       opts.roofAddon.variantId,
        quantity: opts.quantity,
        properties: {
          "Roof Type":                   opts.roofAddon.label,
          "_4mp_roof_service":           "true",
          "_4mp_roof_link":              opts.linkKey,
          "_4mp_roof_parent_variant_id": opts.mainVariantId
        }
      });
    }

    if (opts.install) {
      items.push({
        id:       opts.install.installVariantId,
        quantity: 1,
        properties: {
          "_4mp_install_service":           "true",
          "_4mp_install_link":              opts.linkKey,
          "_4mp_install_parent_variant_id": opts.mainVariantId,
          "_4mp_install_postcode":          opts.install.postcode,
          "_4mp_install_area":              opts.install.area,
          "_4mp_install_crew":              opts.install.crew,
          "_4mp_install_price":             String(opts.install.price)
        }
      });
    }

    if (opts.delivery) {
      items.push({
        id: opts.delivery.variantId,
        quantity: 1,
        properties: {
          "Delivery postcode": opts.delivery.postcode,
          "Delivery calculation": opts.delivery.message,
          "_4mp_delivery_service": "true",
          "_4mp_delivery_link": opts.linkKey,
          "_4mp_delivery_parent_variant_id": opts.mainVariantId,
          "_4mp_delivery_miles": String(opts.delivery.billableMiles),
          "_4mp_delivery_price": String(opts.delivery.chargePence)
        }
      });
    }

    return items;
  }

  function linkedServiceCorrections(items) {
    var corrections = [];

    items.forEach(function (item, index) {
      if (isInstallServiceLine(item) && item.quantity !== 1) {
        corrections.push({ line: index + 1, quantity: 1 });
        return;
      }
      if (isInstallServiceLine(item)) {
        var installLink = getLinkKey(item);
        var hasInstallParent = items.some(function (other) {
          return other !== item &&
            isInstallParentLine(other) &&
            getLinkKey(other) === installLink;
        });
        if (!hasInstallParent) corrections.push({ line: index + 1, quantity: 0 });
        return;
      }
      if (isRoofServiceLine(item)) {
        var roofLink = getRoofLinkKey(item);
        var roofParent = items.find(function (other) {
          return other !== item &&
            isRoofParentLine(other) &&
            getRoofLinkKey(other) === roofLink;
        });
        if (!roofParent) {
          corrections.push({ line: index + 1, quantity: 0 });
        } else if (item.quantity !== roofParent.quantity) {
          corrections.push({ line: index + 1, quantity: roofParent.quantity });
        }
        return;
      }
      if (isDeliveryServiceLine(item) && item.quantity !== 1) {
        corrections.push({ line: index + 1, quantity: 1 });
        return;
      }
      if (isDeliveryServiceLine(item)) {
        var deliveryLink = getDeliveryLinkKey(item);
        var hasDeliveryParent = items.some(function (other) {
          return other !== item &&
            isDeliveryParentLine(other) &&
            getDeliveryLinkKey(other) === deliveryLink;
        });
        if (!hasDeliveryParent) corrections.push({ line: index + 1, quantity: 0 });
      }
    });

    return corrections;
  }

  /* ═══════════════════════════════════════════════════════════════
   *  PDP UI
   * ═══════════════════════════════════════════════════════════════ */

  function bindProductPageUI() {
    /* ── Installation UI elements (may be absent on some products) ── */
    var installBox    = document.querySelector("[data-fmp-install-box]");
    var toggle        = installBox && installBox.querySelector("input[type='checkbox']");
    var fields        = installBox && installBox.querySelector(".fmp-installation-fields");
    var postcodeInput = installBox && installBox.querySelector("[data-fmp-install-postcode]");
    var getPriceBtn   = installBox && installBox.querySelector("[data-fmp-install-getprice]");
    var messageEl     = installBox && installBox.querySelector("[data-fmp-install-message]");

    /* Mileage delivery UI elements (present only when the product metafield is true). */
    var deliveryBox     = document.querySelector("[data-fmp-delivery-box]");
    var deliveryInput   = deliveryBox && deliveryBox.querySelector("[data-fmp-delivery-postcode]");
    var deliveryButton  = deliveryBox && deliveryBox.querySelector("[data-fmp-delivery-calculate]");
    var deliveryResult  = deliveryBox && deliveryBox.querySelector("[data-fmp-delivery-result]");

    /* We need at least a form to attach listeners to */
    var form = (installBox && installBox.closest("form")) ||
               (deliveryBox && deliveryBox.closest("form")) ||
               document.querySelector("form[action*='/cart/add']") ||
               document.querySelector("[data-fmp-install-box]") && null; /* last resort */

    /* Wall mesh is independent of the roof, including on mesh-only products. */
    var hasRoofPicker = !!document.querySelector("[data-fmp-roof-select]");
    var hasMeshChoice = !!(form && form.querySelector("[data-fmp-mesh-choice-group]"));
    if (!installBox && !deliveryBox && !hasRoofPicker && !hasMeshChoice) return;

    /* If installBox exists we need the key sub-elements */
    if (installBox && (!toggle || !fields || !postcodeInput || !messageEl || !form)) {
      console.warn("[4mypet] Installation UI: missing required elements",
        { toggle: !!toggle, fields: !!fields, postcodeInput: !!postcodeInput,
          messageEl: !!messageEl, form: !!form });
      return;
    }
    /* Enquiry-only products have no cart form, but their delivery calculator
       can still return a useful postcode quote. */
    if (deliveryBox && (!deliveryInput || !deliveryButton || !deliveryResult)) {
      console.warn("[4mypet] Delivery UI: missing required elements");
      return;
    }

    var addBtn = form && form.querySelector(".fp-add-btn");

    /* ── State ── */
    var resolvedInstallPrice = null;
    var resolvedDelivery     = null;
    var isAdding             = false;

    /* Variant-aware roof data. Products with one roof matrix continue to use
       the original JSON array. Consolidated size products use a variants map. */
    var roofBox = form && form.querySelector("[data-fmp-roof-box]");
    var roofConfig = null;
    if (roofBox && roofBox.dataset.fmpRoofConfig) {
      try { roofConfig = JSON.parse(roofBox.dataset.fmpRoofConfig); } catch (_) {}
    }

    function roofOptionsForVariant(variantId) {
      if (!roofConfig) return null;
      if (Array.isArray(roofConfig)) return roofConfig;
      var variants = roofConfig.variants || {};
      return variants[String(variantId)] || roofConfig.default || [];
    }

    function renderRoofOptions(variantId) {
      if (!roofBox) return;
      var sel = roofBox.querySelector("[data-fmp-roof-select]");
      if (!sel) return;
      var options = roofOptionsForVariant(variantId);
      if (!options) return;
      var previous = sel.value;
      sel.innerHTML = "";
      options.forEach(function (item) {
        var opt = document.createElement("option");
        var price = Math.round(Number(item.price) || 0);
        opt.value = item.label;
        opt.dataset.fmpRoofPrice = String(price);
        opt.dataset.fmpRoofDescription = item.description || "";
        opt.textContent = roofOptionText(item.label, price);
        sel.appendChild(opt);
      });
      var sameChoice = Array.prototype.some.call(sel.options, function (opt) {
        return opt.value === previous;
      });
      if (sameChoice) sel.value = previous;
      sel.disabled = options.length === 0;
      roofBox.hidden = options.length === 0;
      var description = roofBox.querySelector(".fmp-roof-box__description");
      var selected = sel.options[sel.selectedIndex];
      if (description) {
        description.textContent = selected
          ? selected.dataset.fmpRoofDescription || ""
          : "";
      }
      var message = roofBox.querySelector("#fmp-roof-validation-msg");
      if (message) message.textContent = "";
      updateButtonLabel();
    }

    function shortRoofLabel(label) {
      var text = String(label || "").replace(/^Add\s+/i, "").trim();
      var lower = text.toLowerCase();
      var colour = lower.indexOf("clear") !== -1 ? "Clear" :
        lower.indexOf("black") !== -1 ? "Black" : "";

      if (lower.indexOf("mixed") !== -1 ||
          (lower.indexOf("mesh panel") !== -1 && lower.indexOf("waterproof panel") !== -1)) {
        return (colour ? colour + " " : "") + "Mixed Roof";
      }
      if (lower.indexOf("waterproof") !== -1) {
        return (colour ? colour + " " : "") + "Waterproof Roof";
      }
      if (lower.indexOf("mesh") !== -1) return "Mesh Roof";
      if (lower.indexOf("no roof") !== -1) return "No Roof";
      return text;
    }

    function roofOptionText(label, price) {
      var shortLabel = shortRoofLabel(label);
      if (price === 0 && /no roof|roof included/i.test(label)) return shortLabel;
      return price === 0
        ? shortLabel + " (Included)"
        : shortLabel + " (+\xA3" + price + ")";
    }

    /*
     * Cache the base product price TEXT once at bind time.
     * We must do this before any updateButtonLabel() call can destroy the
     * #fmp-add-price span by setting addBtn.textContent.
     */
    var basePriceEl     = form && form.querySelector("#fmp-add-price");
    var cachedBasePrice = basePriceEl ? basePriceEl.textContent.trim() : "";

    /* Read the roof surcharge price (£) from the selected option, or 0. */
    function readRoofAddonPrice() {
      var sel = form && form.querySelector("[data-fmp-roof-select]");
      if (!sel || !sel.value) return 0;
      var opt = sel.options[sel.selectedIndex];
      return parseFloat(opt.getAttribute("data-fmp-roof-price") || "0") || 0;
    }

    /* ── Button label ── */
    function updateButtonLabel() {
      if (!addBtn) return;
      var installOn = toggle && toggle.checked;
      var roofPrice = readRoofAddonPrice();
      /* Always read the live price from the span so variant changes are reflected.
         Fall back to cachedBasePrice only if the span has not yet been written. */
      var priceSpan    = document.getElementById("fmp-add-price");
      var currentPrice = priceSpan ? priceSpan.textContent.trim() : cachedBasePrice;
      var suffix = "";
      if (roofPrice > 0) {
        suffix += " + \xA3" + roofPrice.toFixed(2).replace(/\.00$/, "") + " roof";
      }
      if (installOn) {
        if (resolvedInstallPrice !== null) {
          suffix += " + \xA3" + resolvedInstallPrice + " install";
        } else {
          suffix += " + install";
        }
      }
      if (resolvedDelivery && resolvedDelivery.charge) {
        suffix += " + " + resolvedDelivery.charge + " delivery";
      }
      /* Always use innerHTML so the #fmp-add-price span is preserved.
         This lets updateAddBtnPrice() (product-page JS) keep the displayed
         price in sync when the customer switches variant. */
      addBtn.innerHTML =
        "Add to Basket \u2014 <span id=\"fmp-add-price\">" + currentPrice + "</span>" + suffix;
    }

    function setMessage(text, type) {
      if (!messageEl) return;
      messageEl.textContent = text;
      messageEl.className = "fmp-installation-message" +
        (type === "ok"  ? " fmp-installation-message--ok"  :
         type === "err" ? " fmp-installation-message--err" : "");
    }

    function setDeliveryMessage(text, type, allowHtml) {
      if (!deliveryResult) return;
      if (allowHtml) deliveryResult.innerHTML = text;
      else deliveryResult.textContent = text;
      deliveryResult.className = "fmp-mileage-delivery__result" +
        (type === "ok" ? " fmp-mileage-delivery__result--ok" :
         type === "err" ? " fmp-mileage-delivery__result--err" : "");
    }

    async function calculateDelivery() {
      if (!deliveryBox) return null;
      var postcode = deliveryInput.value.trim();
      if (!postcode) {
        resolvedDelivery = null;
        setDeliveryMessage("Please enter your delivery postcode.", "err");
        updateButtonLabel();
        return null;
      }

      deliveryButton.disabled = true;
      deliveryButton.textContent = "Calculating\u2026";
      setDeliveryMessage("Calculating the driving route from Outwell\u2026", "");

      try {
        var response = await _fetch(deliveryBox.dataset.apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            postcode: postcode,
            productHandle: deliveryBox.dataset.productHandle
          })
        });
        var data = await response.json();
        if (!response.ok) throw new Error(data.error || "Could not calculate delivery.");

        if (data.status === "manual_quote") {
          resolvedDelivery = null;
          var productTitle = document.querySelector(".fp-title");
          setDeliveryMessage(
            data.message + " <a href=\"/pages/contact?product=" +
            encodeURIComponent(productTitle ? productTitle.textContent.trim() : "Delivery quote") +
            "&handle=" + encodeURIComponent(deliveryBox.dataset.productHandle) +
            "\">Request a delivery quote</a>.",
            "err",
            true
          );
          updateButtonLabel();
          return data;
        }

        resolvedDelivery = data;
        deliveryInput.value = data.postcode;
        setDeliveryMessage(
          "Delivery: " + data.charge + ". " + data.message +
          " (live route " + data.oneWayMiles + " miles one way, rounded to the " +
          data.billableMiles + "-mile band).",
          "ok"
        );
        updateButtonLabel();
        return data;
      } catch (error) {
        resolvedDelivery = null;
        setDeliveryMessage(
          error.message || "Could not calculate delivery. Please try again.",
          "err"
        );
        updateButtonLabel();
        return null;
      } finally {
        deliveryButton.disabled = false;
        deliveryButton.textContent = "Calculate";
      }
    }

    if (deliveryButton) deliveryButton.addEventListener("click", calculateDelivery);
    if (deliveryInput) {
      deliveryInput.addEventListener("input", function () {
        resolvedDelivery = null;
        setDeliveryMessage("", "");
        updateButtonLabel();
      });
      deliveryInput.addEventListener("keydown", function (event) {
        if (event.key === "Enter") {
          event.preventDefault();
          calculateDelivery();
        }
      });
    }

    /* ── Installation checkbox ── */
    if (toggle) {
      toggle.addEventListener("change", function () {
        if (fields) fields.hidden = !toggle.checked;
        if (!toggle.checked) { setMessage("", ""); resolvedInstallPrice = null; }
        updateButtonLabel();
      });
    }

    /* ── Get install price ── */
    function showInstallPrice() {
      if (!installBox) return;
      var result = resolvePostcodePrice({
        installType: installBox.dataset.installType,
        postcode:    postcodeInput.value
      });
      if (result.ok) {
        resolvedInstallPrice = result.price;
        setMessage(
          "Installation for " + result.area + ": \xA3" + result.price,
          "ok"
        );
      } else if (result.code === "quote_only") {
        resolvedInstallPrice = null;
        setMessage(
          "Installation in your area (" + result.area + ") is quoted manually. " +
          "You can still add this to your basket \u2014 we'll contact you to agree " +
          "the delivery & installation cost.",
          ""
        );
      } else {
        resolvedInstallPrice = null;
        setMessage(result.message, "err");
      }
      updateButtonLabel();
    }

    if (getPriceBtn) getPriceBtn.addEventListener("click", showInstallPrice);
    if (postcodeInput) {
      postcodeInput.addEventListener("keydown", function (e) {
        if (e.key === "Enter") { e.preventDefault(); showInstallPrice(); }
      });
    }

    /* Refresh button label when the roof select changes */
    if (form) {
      form.addEventListener("change", function (e) {
        if (e.target && e.target.hasAttribute("data-fmp-roof-select")) {
          var roofMsg = form.querySelector("#fmp-roof-validation-msg");
          if (roofMsg) roofMsg.textContent = "";
          var description = form.querySelector(".fmp-roof-box__description");
          var selected = e.target.options[e.target.selectedIndex];
          if (description) {
            description.textContent = selected
              ? selected.dataset.fmpRoofDescription || ""
              : "";
          }
          updateButtonLabel();
        }
      });
    }

    document.addEventListener("fmp:variant-changed", function (event) {
      if (event.detail && event.detail.variantId) {
        renderRoofOptions(event.detail.variantId);
      }
    });

    /* ─────────────────────────────────────────────────────────────
     *  Shared add-to-cart logic
     *  Called from both the click capture and the form-submit fallback.
     *  Returns true if it handled the add (caller should stop propagation).
     * ──────────────────────────────────────────────────────────── */
    async function handleAddToCart() {
      if (isAdding) return false;
      var installOn  = !!(toggle && toggle.checked);
      var roofSelect = form.querySelector("[data-fmp-roof-select]");
      var roofExists = !!(roofSelect && !roofSelect.disabled);

      /* 1. Validate roof if picker present */
      if (!roofIsValid(form)) {
        var roofMsg = form.querySelector("#fmp-roof-validation-msg");
        if (roofMsg) roofMsg.textContent = "Please select a roof type before adding to basket.";
        var roofSel = form.querySelector("[data-fmp-roof-select]");
        if (roofSel) roofSel.focus();
        return false; /* signal: do not proceed */
      }

      /* 2. If installation is selected, validate postcode.
         Manual-quote areas (Highlands / offshore / Isle of Wight / Isle of Man)
         are allowed through: we add the product with a "cost to be agreed" note
         instead of a priced installation line. */
      var quoteOnlyInfo = null;
      if (installOn) {
        var priceCheck = resolvePostcodePrice({
          installType: installBox.dataset.installType,
          postcode:    postcodeInput.value
        });
        if (!priceCheck.ok) {
          if (priceCheck.code === "quote_only") {
            quoteOnlyInfo = priceCheck;
          } else {
            setMessage(priceCheck.message, "err");
            postcodeInput.focus();
            return false;
          }
        }
      }

      /* Mileage delivery is mandatory on products where the panel is rendered. */
      if (deliveryBox) {
        if (!resolvedDelivery) {
          var deliveryCheck = await calculateDelivery();
          if (!deliveryCheck || deliveryCheck.status !== "quoted") {
            if (deliveryInput) deliveryInput.focus();
            return false;
          }
        }

        var existingCart = await fetchCart();
        if (existingCart.items.some(function (item) {
          return isDeliveryParentLine(item) || isDeliveryServiceLine(item);
        })) {
          setDeliveryMessage(
            "Only one mileage-delivery product can be ordered online at a time. " +
            "Please complete this order separately or contact us.",
            "err"
          );
          return false;
        }
      }

      /* 3. Lock UI */
      var savedHtml = addBtn ? addBtn.innerHTML : null;
      isAdding = true;
      if (addBtn) { addBtn.disabled = true; addBtn.textContent = "Adding\u2026"; }
      if (installOn && !quoteOnlyInfo) setMessage("Looking up installation\u2026", "");

      try {
        /* 4. Optionally resolve install variant (skipped for manual-quote areas) */
        var install = null;
        if (installOn && !quoteOnlyInfo) {
          install = await resolveInstallationForProduct({
            installType: installBox.dataset.installType,
            postcode:    postcodeInput.value
          });
          if (!install.ok) throw new Error(install.message);
        }

        /* 5. Read form data */
        var variantInput  = form.querySelector("[name='id']");
        var quantityInput = form.querySelector("[name='quantity']");
        var mainVariantId = variantInput && String(variantInput.value);
        var quantity      = parseInt((quantityInput && quantityInput.value) || "1", 10);
        if (!mainVariantId) {
          throw new Error("Please select an available product option before adding to basket.");
        }

        /* 6. Read the chosen roof add-on (async — may fetch live for new price bands) */
        var roofAddon = roofExists ? await readRoofAddon(form) : null;

        /* Guard: a paid roof option must resolve to a variant, otherwise the
           surcharge line is silently skipped and the customer gets the roof
           free. Block the add and surface a clear message instead. */
        if (roofAddon && roofAddon.price > 0 && !roofAddon.variantId) {
          if (addBtn) {
            addBtn.disabled = false;
            addBtn.innerHTML = savedHtml || "Add to Basket";
          }
          var rUnavail = form.querySelector("#fmp-roof-validation-msg");
          if (rUnavail) {
            rUnavail.textContent =
              "Sorry, this roof option is temporarily unavailable. Please contact us to order.";
            rUnavail.style.color = "#b00";
          }
          setMessage("Roof option unavailable \u2014 please contact us.", "err");
          return false;
        }

        /* 7. Build main product properties */
        var mainProps = {};

        /* Wall mesh finish is independent of roof type and timber colour. */
        var meshChoice = form.querySelector("[data-fmp-mesh-choice]:checked");
        if (meshChoice && !meshChoice.disabled) {
          mainProps["Mesh Choice"] = meshChoice.value;
        }

        /* A shared link key ties the catio (parent) to its roof + install
           service lines so the cart guard can cascade-remove them when the
           parent catio is removed. Generated whenever a paid roof OR an
           installation is attached. */
        var hasPaidRoof = !!(roofAddon && roofAddon.price > 0 && roofAddon.variantId);
        var linkKey = (hasPaidRoof || install || resolvedDelivery)
          ? "fmp_" + Date.now() + "_" + Math.random().toString(36).slice(2, 8)
          : null;

        /* Roof Type line-item property (always written when a selection was made) */
        if (roofAddon) {
          mainProps["Roof Type"] = roofAddon.price > 0
            ? roofAddon.label + " (+\xA3" + roofAddon.price + ")"
            : roofAddon.label;
        }

        /* Mark the parent so the guard can match its roof service line */
        if (hasPaidRoof) {
          mainProps["_4mp_roof_requested"] = "yes";
          mainProps["_4mp_roof_link"]      = linkKey;
        }

        /* Installation properties */
        if (install) {
          Object.assign(mainProps, {
            "_4mp_install_requested":  "yes",
            "_4mp_install_link":       linkKey,
            "_4mp_install_postcode":   install.postcode,
            "_4mp_install_area":       install.area,
            "_4mp_install_crew":       install.crew,
            "_4mp_install_price":      String(install.price),
            "_4mp_install_variant_id": String(install.installVariantId)
          });
        }

        /* Manual-quote area: no priced installation line is added. Record a
           clear note on the catio line so the customer (and our team) can see
           the delivery & installation cost still has to be agreed. */
        if (quoteOnlyInfo) {
          mainProps["Installation"] =
            "Requested for " + quoteOnlyInfo.postcode;
          mainProps["Delivery & installation cost"] =
            "To be agreed \u2014 your area is quoted manually. We'll contact you to confirm.";
        }

        if (resolvedDelivery) {
          Object.assign(mainProps, {
            "_4mp_delivery_requested":  "yes",
            "_4mp_delivery_link":       linkKey,
            "_4mp_delivery_postcode":   resolvedDelivery.postcode,
            "_4mp_delivery_miles":      String(resolvedDelivery.billableMiles),
            "_4mp_delivery_price":      String(resolvedDelivery.chargePence),
            "_4mp_delivery_variant_id": String(resolvedDelivery.variantId)
          });
        }

        /* 8. Build items array */
        var items = buildLinkedCartItems({
          mainVariantId: mainVariantId,
          quantity: quantity,
          mainProps: mainProps,
          hasPaidRoof: hasPaidRoof,
          roofAddon: roofAddon,
          install: install,
          delivery: resolvedDelivery,
          linkKey: linkKey
        });

        /* 9. Single combined cart/add.js request */
        await cartAdd(items);
        window.location.href = shopifyRoot() + "cart";
        return true;

      } catch (err) {
        setMessage(err.message, "err");
        if (deliveryResult) setDeliveryMessage(err.message, "err");
        /* Always show basket errors beside the visible roof control. Installation
           messages can be inside hidden fields when installation is unchecked. */
        var roofMsg = form && form.querySelector("#fmp-roof-validation-msg");
        if (roofMsg) {
          roofMsg.textContent = err.message || "Could not add to basket. Please try again.";
          roofMsg.style.color = "#b00";
        }
        if (addBtn) {
          addBtn.disabled = false;
          addBtn.innerHTML = savedHtml || "Add to Basket";
        }
        return false;
      } finally {
        isAdding = false;
      }
    }

    /* ─────────────────────────────────────────────────────────────
     *  Button-click capture
     *
     *  Intercepts when:
     *    • A roof picker is present on this product (always), OR
     *    • Installation has been selected by the customer, OR
     *    • Wall mesh choices or mileage delivery are present.
     *
     *  With no custom options to handle, native submission runs unchanged.
     * ──────────────────────────────────────────────────────────── */
    if (addBtn) {
      addBtn.addEventListener("click", async function (event) {
        var roofPickerPresent = !!form.querySelector("[data-fmp-roof-select]");
        var installOn         = !!(toggle && toggle.checked);
        var deliveryRequired  = !!deliveryBox;

        /* Pass through if we have nothing to handle */
        if (!roofPickerPresent && !installOn && !deliveryRequired && !hasMeshChoice) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        await handleAddToCart();
      }, true /* capture — fires first */);
    }

    /* ─────────────────────────────────────────────────────────────
     *  Fallback: form-submit interceptor
     *  Same logic for submit events (keyboard / accessibility triggers).
     * ──────────────────────────────────────────────────────────── */
    if (form) {
      form.addEventListener("submit", async function (event) {
        var roofPickerPresent = !!form.querySelector("[data-fmp-roof-select]");
        var installOn         = !!(toggle && toggle.checked);
        var deliveryRequired  = !!deliveryBox;

        if (!roofPickerPresent && !installOn && !deliveryRequired && !hasMeshChoice) return;

        event.preventDefault();
        await handleAddToCart();
      });
    }
  }

  /* ═══════════════════════════════════════════════════════════════
   *  Cart guard
   * ═══════════════════════════════════════════════════════════════ */

  async function runCartGuard() {
    if (!document.querySelector(
      "cart-items-component, .cart-page, .cart-drawer, #cart-form"
    )) return;

    var cart;
    try { cart = await fetchCart(); } catch (_) { return; }

    var mutated = false;
    var items   = cart.items.slice();

    var corrections = linkedServiceCorrections(items).sort(function (a, b) {
      return b.line - a.line;
    });
    for (var i = 0; i < corrections.length; i++) {
      await changeCartLine(corrections[i].line, corrections[i].quantity);
      mutated = true;
    }

    var svcCnt = items.filter(isInstallServiceLine).length;
    var parCnt = items.filter(isInstallParentLine).length;
    var deliveryServices = items.filter(isDeliveryServiceLine);
    var deliveryParents  = items.filter(isDeliveryParentLine);
    var missingDeliveryFee = deliveryParents.some(function (parent) {
      return !deliveryServices.some(function (service) {
        return getDeliveryLinkKey(service) === getDeliveryLinkKey(parent);
      });
    });
    if (svcCnt > 1 || parCnt > 1) {
      disableCheckout("Online installation is limited to one product per order. " +
        "Please contact us for multiple installations.");
    } else if (
      deliveryServices.length > 1 ||
      deliveryParents.length > 1 ||
      missingDeliveryFee
    ) {
      disableCheckout(
        "This order is missing its required stable delivery charge. " +
        "Please remove the stable and add it again with a delivery postcode."
      );
    } else {
      clearCheckoutBlock();
    }

    if (mutated) {
      document.dispatchEvent(new CustomEvent("cart:update", { bubbles: true }));
      window.dispatchEvent(new CustomEvent("cart:refresh"));
    }
    bindParentRemovalLinks(items);
  }

  function disableCheckout(msg) {
    document.querySelectorAll(
      "button[name='checkout'], a[href$='/checkout'], " +
      ".cart-summary-block__checkout-button, [data-checkout-button]"
    ).forEach(function (b) {
      b.setAttribute("disabled", "disabled"); b.setAttribute("aria-disabled", "true");
      b.style.opacity = "0.5"; b.style.pointerEvents = "none";
    });
    var banner = document.querySelector("[data-fmp-install-guard]");
    if (!banner) {
      banner = document.createElement("div");
      banner.setAttribute("data-fmp-install-guard", "true");
      banner.style.cssText =
        "background:#c08959;color:#faf8f5;padding:1rem 1.25rem;" +
        "border-radius:.5rem;margin:1rem 0;font-size:.95rem;line-height:1.5";
      var host = document.querySelector(
        ".cart-page__summary,.cart-page__items,.cart-page,.cart-drawer") || document.body;
      host.prepend(banner);
    }
    banner.textContent = msg;
  }

  function clearCheckoutBlock() {
    var banner = document.querySelector("[data-fmp-install-guard]");
    if (banner) banner.remove();
    document.querySelectorAll(
      "button[name='checkout'], a[href$='/checkout'], " +
      ".cart-summary-block__checkout-button, [data-checkout-button]"
    ).forEach(function (b) {
      b.removeAttribute("disabled"); b.removeAttribute("aria-disabled");
      b.style.opacity = b.style.pointerEvents = "";
    });
  }

  function bindParentRemovalLinks(items) {
    document.querySelectorAll("[data-key]").forEach(function (row) {
      if (row.dataset.fmpInstallBound === "true") return;
      var key    = row.getAttribute("data-key");
      var parent = items.find(function (it) {
        return it.key === key && (
          isInstallParentLine(it) ||
          isRoofParentLine(it) ||
          isDeliveryParentLine(it)
        );
      });
      if (!parent) return;
      var ilk    = getLinkKey(parent);      /* installation link */
      var rlk    = getRoofLinkKey(parent);  /* roof link */
      var dlk    = getDeliveryLinkKey(parent); /* delivery link */
      var btn    = row.querySelector(".cart-items__remove, button[aria-label*='Remove' i]");
      if (!btn) return;
      row.dataset.fmpInstallBound = "true";

      btn.addEventListener("click", async function (e) {
        /*
         * We take full ownership of this removal so there is no race between
         * our async cleanup and Shopify's concurrent cart/change request.
         *
         * stopPropagation() prevents the event from bubbling up to the
         * cart-items-component's delegated listener (Shopify's onLineItemRemove),
         * which would fire its own cart/change request in parallel.  Without this,
         * Shopify's morphSection can re-render the cart while our changeCartLine
         * calls are still in flight, producing a stale DOM that still shows the
         * orphaned install/roof line even though the server cart is already clean.
         */
        e.stopPropagation();

        /* Animate the parent row out immediately for visual feedback */
        row.style.setProperty("--row-height", row.clientHeight + "px");
        row.classList.add("removing");

        try {
          /* 1. Remove installation service line */
          if (ilk) {
            var f1  = await fetchCart();
            var idx = f1.items.findIndex(function (it) {
              return isInstallServiceLine(it) && getLinkKey(it) === ilk;
            });
            if (idx >= 0) await changeCartLine(idx + 1, 0);
          }

          /* 2. Remove roof service line */
          if (rlk) {
            var f2   = await fetchCart();
            var idxR = f2.items.findIndex(function (it) {
              return isRoofServiceLine(it) && getRoofLinkKey(it) === rlk;
            });
            if (idxR >= 0) await changeCartLine(idxR + 1, 0);
          }

          /* 3. Remove mileage delivery service line */
          if (dlk) {
            var fD = await fetchCart();
            var idxD = fD.items.findIndex(function (it) {
              return isDeliveryServiceLine(it) && getDeliveryLinkKey(it) === dlk;
            });
            if (idxD >= 0) await changeCartLine(idxD + 1, 0);
          }

          /* 4. Remove the parent itself, re-fetching so the line index is valid */
          var f3       = await fetchCart();
          var catioIdx = f3.items.findIndex(function (it) { return it.key === key; });
          if (catioIdx >= 0) await changeCartLine(catioIdx + 1, 0);

          /*
           * 4. Trigger a full section re-render.
           *    Dispatching cart:update without sections data causes
           *    Shopify's cart-items-component to call sectionRenderer.renderSection,
           *    which re-fetches the section HTML and morphs the DOM to match the
           *    current (now fully-clean) server cart.
           */
          document.dispatchEvent(new CustomEvent("cart:update", {
            bubbles: true,
            detail:  { data: {} }
          }));
        } catch (_) {
          /* Something went wrong — reload to recover to a consistent state */
          window.location.reload();
        }
      }, { capture: true });
    });
  }

  /* ═══════════════════════════════════════════════════════════════
   *  Boot
   * ═══════════════════════════════════════════════════════════════ */

  function boot() { bindProductPageUI(); runCartGuard(); }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  document.addEventListener("cart:update", function () { setTimeout(runCartGuard, 50); });
  window.addEventListener("cart:refresh",  function () { setTimeout(runCartGuard, 50); });

  window.FMP_Install = {
    normalisePostcode:             normalisePostcode,
    extractPostcodeArea:           extractPostcodeArea,
    resolvePostcodePrice:          resolvePostcodePrice,
    resolveInstallationForProduct: resolveInstallationForProduct,
    runCartGuard:                  runCartGuard,
    buildLinkedCartItems:          buildLinkedCartItems,
    linkedServiceCorrections:      linkedServiceCorrections
  };
})();
